import { cva } from "class-variance-authority";
import React from "react";

import { Option } from "../../../types/Option";

import { cn } from "../../../lib/utils";

const selectionPillVariants = cva(
	"box-border flex min-h-8 w-fit cursor-pointer items-center justify-center gap-2 rounded-1 border border-solid border-neutral-3 bg-neutral-0 px-2 text-left text-paragraph-3 font-semi-bold text-neutral-7 hover:border-neutral-5 active:translate-y-px",
	{
		variants: {
			errored: {
				true: "border-errored text-errored hover:border-errored",
			},
			disabled: {
				true: "cursor-not-allowed opacity-disabled hover:border-neutral-3",
			},
			selected: {
				true: "bg-neutral-2",
			},
		},
		defaultVariants: {
			disabled: false,
			errored: false,
			selected: false,
		},
	}
);

type SelectionPillType = "checkbox" | "radio";

export type SelectionPillProps<T> = {
	/**
	 * The option to display in the selection pill
	 */
	option: Option<T>;
	/**
	 * The function to call when the option is selected
	 */
	onSelect: (option: T) => void;
	/**
	 * The function to call when the option is deselected
	 */
	onDeselect: (option: T) => void;
	/**
	 * Whether the option is selected
	 */
	selected: boolean;
	/**
	 * The type of selection pill
	 */
	type: SelectionPillType;
	/**
	 * Whether the selection pill is disabled
	 */
	disabled: boolean;
	/**
	 * Whether the selection pill has an error
	 */
	errored: boolean;
	/**
	 * The id of the selection pill
	 */
	id: string;
	/**
	 * The name of the selection pill
	 */
	name?: string;
	/**
	 * The aria-describedby attribute of the selection pill
	 */
	"aria-describedby"?: string;
	/**
	 * The function to call when a key is pressed
	 */
	onKeyDown?: (e: React.KeyboardEvent) => void;
	/**
	 * The tab index of the selection pill
	 */
	tabIndex?: number;
};

/**
 * A selection pill component this is an atom used by the Tag Select components to display the options. it can be used as a radio button or checkbox depending on its context.
 */
export const SelectionPill = <T,>({
	option,
	onSelect,
	onDeselect,
	selected,
	type,
	disabled,
	errored,
	id,
	name,
	"aria-describedby": ariaDescribedby,
	onKeyDown,
	tabIndex,
}: SelectionPillProps<T>) => {
	const handleClick = () => {
		if (disabled || option.disabled) return;

		if (selected) {
			onDeselect(option.value as T);
		} else {
			onSelect(option.value as T);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			handleClick();
		}

		// Call parent onKeyDown if provided
		if (onKeyDown) {
			onKeyDown(e);
		}
	};

	return (
		<button
			type="button"
			id={id}
			name={name}
			role={type === "radio" ? "radio" : "checkbox"}
			aria-checked={selected}
			aria-disabled={disabled || option.disabled}
			aria-describedby={ariaDescribedby}
			disabled={disabled || option.disabled}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			className={cn(selectionPillVariants({ errored, disabled, selected }))}
			tabIndex={tabIndex}
		>
			{option.label}
			{option.subtext && (
				<div
					id={`${id}-desc`}
					className="absolute -m-px size-px overflow-hidden border-0 p-0 whitespace-nowrap"
				>
					{option.subtext}
				</div>
			)}
		</button>
	);
};

SelectionPill.displayName = "SelectionPill";
export default SelectionPill;
